"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BackgroundColor = void 0;
const SystemUI = __importStar(require("expo-system-ui"));
const React = __importStar(require("react"));
const react_native_1 = require("react-native");
const propsStack = [];
const defaultProps = createStackEntry({
    color: '#fff',
});
// Timer for updating the native module values at the end of the frame.
let updateImmediate = null;
let appearanceListener = null;
let appStateListener = null;
/**
 * A stack based component for setting the background color of the root view.
 * Useful for changing the background color on certain screens or inside of native modals.
 * Updates based on Appearance and AppState.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <>
 *       <BackgroundColor color={{ light: '#fff', dark: '#000' }} />
 *       <BackgroundColor color={'#fff000'} />
 *     </>
 *   )
 * }
 * ```
 */
function BackgroundColor(props) {
    let stack = React.useRef(null);
    React.useEffect(() => {
        // Create a stack entry on component mount
        stack.current = BackgroundColor.pushStackEntry(props);
        return () => {
            if (stack.current) {
                // Update on component unmount
                BackgroundColor.popStackEntry(stack.current);
            }
        };
    }, []);
    React.useEffect(() => {
        if (stack.current) {
            // Update the current stack entry
            stack.current = BackgroundColor.replaceStackEntry(stack.current, props);
        }
    }, [props.color]);
    return null;
}
exports.BackgroundColor = BackgroundColor;
function isThemedColor(color) {
    return !!color && typeof color !== 'string' && ('light' in color) && ('dark' in color);
}
/**
 * Merges the prop stack with the default values.
 */
function mergePropsStack(propsStack, defaultValues) {
    return propsStack.reduce((prev, cur) => {
        for (const prop in cur) {
            // @ts-ignore
            if (cur[prop] != null) {
                // @ts-ignore
                prev[prop] = cur[prop];
            }
        }
        return prev;
    }, Object.assign({}, defaultValues));
}
function setColorAsync(scheme, color) {
    var _a, _b;
    if (isThemedColor(color)) {
        return SystemUI.setBackgroundColorAsync(scheme === 'dark' ? (_a = color.dark) !== null && _a !== void 0 ? _a : '#000' : (_b = color.light) !== null && _b !== void 0 ? _b : '#fff');
    }
    return SystemUI.setBackgroundColorAsync(color !== null && color !== void 0 ? color : '#fff');
}
/**
 * Returns an object to insert in the props stack from the props
 * and the transition/animation info.
 */
function createStackEntry(props) {
    return {
        color: props.color
    };
}
/**
 * Set the background color for the app
 * @param color Background color.
 * @param animated Animate the style change.
 */
BackgroundColor.setColor = (color) => {
    defaultProps.color = color;
    setColorAsync(react_native_1.Appearance.getColorScheme(), color);
};
/**
 * Push a BackgroundColor entry onto the stack.
 * The return value should be passed to `popStackEntry` when complete.
 *
 * @param props Object containing the BackgroundColor props to use in the stack entry.
 */
BackgroundColor.pushStackEntry = (props) => {
    const entry = createStackEntry(props);
    propsStack.push(entry);
    // Ensure we only have one appearance change listener.
    if (!appearanceListener) {
        appearanceListener = ({ colorScheme }) => {
            setColorAsync(colorScheme, propsStack[propsStack.length - 1].color);
        };
        react_native_1.Appearance.addChangeListener(appearanceListener);
    }
    if (!appStateListener) {
        appStateListener = () => {
            setColorAsync(react_native_1.Appearance.getColorScheme(), propsStack[propsStack.length - 1].color);
        };
        react_native_1.AppState.addEventListener('change', appStateListener);
    }
    BackgroundColor._updatePropsStack();
    return entry;
};
/**
 * Pop a BackgroundColor entry from the stack.
 *
 * @param entry Entry returned from `pushStackEntry`.
 */
BackgroundColor.popStackEntry = (entry) => {
    const index = propsStack.indexOf(entry);
    if (index !== -1) {
        propsStack.splice(index, 1);
    }
    if (propsStack.length === 0) {
        if (appearanceListener) {
            react_native_1.Appearance.removeChangeListener(appearanceListener);
            appearanceListener = null;
        }
        if (appStateListener) {
            react_native_1.AppState.removeEventListener('change', appStateListener);
            appStateListener = null;
        }
    }
    BackgroundColor._updatePropsStack();
};
/**
 * Replace an existing BackgroundColor stack entry with new props.
 *
 * @param entry Entry returned from `pushStackEntry` to replace.
 * @param props Object containing the BackgroundColor props to use in the replacement stack entry.
 */
BackgroundColor.replaceStackEntry = (entry, props) => {
    const newEntry = createStackEntry(props);
    const index = propsStack.indexOf(entry);
    if (index !== -1) {
        propsStack[index] = newEntry;
    }
    BackgroundColor._updatePropsStack();
    return newEntry;
};
/**
 * Updates the native background color with the props from the stack.
 */
BackgroundColor._updatePropsStack = () => {
    // Send the update to the native module only once at the end of the frame.
    clearImmediate(updateImmediate);
    updateImmediate = setImmediate(() => {
        const { color } = mergePropsStack(propsStack, defaultProps);
        if (color) {
            setColorAsync(react_native_1.Appearance.getColorScheme(), color);
        }
    });
};
//# sourceMappingURL=index.js.map